import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ODataServiceFactory } from 'angular-odata';
import { forkJoin, of, Subject, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  CertifiableSystem,
  Contact,
  PretreatmentSystem,
  ProductionSite,
  WorkflowDocument,
} from '@workspace-app/odatabackend/QualicoatBackendModel';
import { ProductionSiteDataService } from '../../../../crm/shared/data-services/production-site-data.service';
import {
  ActionMenuItem,
  ActionsMenuComponent,
} from '../../../../shared/components/actions-menu/actions-menu.component';
import { getEffectiveApprovedSince, ToastService } from '../../../../shared';
import { PermissionService } from '../../../../shared/services/permission.service';
import { DocumentService } from '../../../../shared/services/document.service';
import { User } from '../../../../shared/interfaces/user.interface';
import { selectUser } from '../../../../state/global';
import { ApprovedSystemsService } from '../../services/approved-systems.service';
import { ApprovedSystemsGridService } from '../../services/approved-systems-grid.service';
import {
  TransitionDialogComponent,
  TransitionDialogData,
  TransitionDialogResult,
} from '../../components/transition-dialog/transition-dialog.component';
import {
  PowderAttributesSectionComponent,
  CoatingSystemData,
  CoatingSystemColor,
} from '../../components/powder-attributes-section/powder-attributes-section.component';
import type { ColorEditorData } from '../../components/coating-system-color-editor/coating-system-color-editor.component';
import { DocumentTypes, EntityTypes } from '../../../../shared/constants/document-types';
import { DocumentUploaderComponent } from '../../../../shared/components/document-uploader/document-uploader.component';
import { InlineTextInputComponent } from '../../../../shared/forms/inline-form-controls/inline-text-input.component';
import { LanguageDropdownComponent } from '../../../../shared/components/language-dropdown/language-dropdown.component';
import { BillsTableComponent } from '../../../../crm/pages/organisation/shared/bills-table/bills-table.component';

@Component({
  selector: 'app-approved-system-view',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    DividerModule,
    TooltipModule,
    ProgressSpinner,
    AccordionModule,
    ActionsMenuComponent,
    PowderAttributesSectionComponent,
    FontAwesomeModule,
    DocumentUploaderComponent,
    InlineTextInputComponent,
    LanguageDropdownComponent,
    BillsTableComponent,
  ],
  providers: [DatePipe, DialogService],
  templateUrl: './approved-system-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./approved-system-view.component.scss'],
})
export class ApprovedSystemViewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private store = inject(Store);
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private permissionService = inject(PermissionService);
  private datePipe = inject(DatePipe);
  private documentService = inject(DocumentService);
  private odataFactory = inject(ODataServiceFactory);
  private approvedSystemsService = inject(ApprovedSystemsService);
  private approvedSystemsGridService = inject(ApprovedSystemsGridService);
  private productionSiteDataService = inject(ProductionSiteDataService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);
  private destroy$ = new Subject<void>();

  // Input for when component is used in overlay mode
  systemInput = input<CertifiableSystem | undefined>(undefined);
  readonly getEffectiveApprovedSince = getEffectiveApprovedSince;

  loadedSystem = signal<CertifiableSystem | null>(null);
  loading = signal(false);
  generatingCertificate = signal(false);
  currentUser = signal<User | undefined>(undefined);
  certificates = signal<WorkflowDocument[]>([]);
  latestCertificate = computed(() => this.certificates()[0] ?? null);
  olderCertificates = computed(() => this.certificates().slice(1));
  documents = signal<WorkflowDocument[]>([]);
  selectedFiles: File[] = [];
  uploadingDocuments = signal(false);

  // Computed signal: use input system if provided, otherwise use loaded system
  system = computed(() => this.systemInput() ?? this.loadedSystem());

  // Running Florida Reports section (QCT only). Count is fetched eagerly (cheap $count); the row list
  // is lazy-loaded on first expand and cached for the session.
  isQct = computed(() =>
    this.permissionService.hasAnyEffectiveRole(this.currentUser(), ['SuperAdmin', 'QCT-Admin', 'QCT-User'])
  );
  /** Members of a laboratory flagged responsible for Florida reports may also see the section. */
  private isFloridaLab = computed(() =>
    this.permissionService.hasAnyOutdoorExposureLabRole(this.currentUser(), ['Florida'])
  );
  /** Section is visible to QCT users and to members of a Florida-responsible laboratory. */
  canSeeFlorida = computed(() => this.isQct() || this.isFloridaLab());
  runningFloridaCount = signal<number | null>(null);
  runningFloridaReports = signal<RunningFloridaRow[]>([]);
  private floridaLoaded = signal(false);

  // Computed action menu items
  actionMenuItems = computed(() => this.buildActionMenuItems());

  constructor() {
    // Load certificates and documents when system changes
    effect(() => {
      const sys = this.system();
      if (sys) {
        untracked(() => {
          this.loadCertificates();
          this.loadDocuments();
          this.loadRunningFloridaCount();
        });
      }
    });
  }

  ngOnInit(): void {
    // Get current user
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe((user) => {
        this.currentUser.set(user);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSystem(id: string, notifyGrid = false): void {
    this.loading.set(true);

    const service = this.odataFactory.entitySet<CertifiableSystem>('CertifiableSystems');
    service
      .entity(id)
      .query((q) => {
        q.expand('ProductionSite($expand=Customer)');
      })
      .fetch()
      .subscribe({
        next: (response) => {
          if (response.entity) {
            this.loadedSystem.set(response.entity);
            // Notify grid service if requested (after updates)
            if (notifyGrid) {
              this.approvedSystemsGridService.notifySystemUpdated(response.entity);
            }
          } else {
            this.toastService.displayErrorMessage('System not found');
          }
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Failed to load system:', error);
          this.toastService.displayErrorMessage('Failed to load system');
          this.loading.set(false);
        },
      });
  }

  private loadCertificates(): void {
    const sys = this.system();
    if (!sys) return;

    const documentsService = this.odataFactory.entitySet<WorkflowDocument>('WorkflowDocuments');
    documentsService
      .entities()
      .query((q) => {
        q.filter(
          `EntityType eq '${EntityTypes.CertifiableSystem}' and EntityId eq ${sys.id} and DocumentType eq '${DocumentTypes.Certificate}'`
        );
        q.orderBy('CreatedAt desc');
      })
      .fetch()
      .subscribe({
        next: (response) => {
          this.certificates.set(response.entities || []);
        },
        error: (error) => {
          console.error('Failed to load certificates:', error);
        },
      });
  }

  // --- Running Florida Reports ---

  private get runningFloridaFilter(): string | null {
    const sys = this.system();
    if (!sys?.id) return null;
    return `coatingSystemId eq ${sys.id} and (state eq 'PendingInitial' or state eq 'ActiveExposure')`;
  }

  /** QCT sees the full projection; the responsible lab uses the anonymized assignments endpoint. */
  private get floridaEntitySet(): string {
    return this.isQct() ? 'FloridaReports' : 'MyFloridaAssignments';
  }

  /** Eager, cheap count so the section header shows "(N)" without fetching the rows. */
  private loadRunningFloridaCount(): void {
    this.floridaLoaded.set(false);
    this.runningFloridaReports.set([]);
    this.runningFloridaCount.set(null);

    const filter = this.runningFloridaFilter;
    if (!filter || !this.canSeeFlorida()) return;

    const params = new HttpParams().set('$filter', filter).set('$count', 'true').set('$top', '0');
    this.http.get<{ '@odata.count'?: number }>(`odata/${this.floridaEntitySet}`, { params }).subscribe({
      next: (res) => this.runningFloridaCount.set(res['@odata.count'] ?? 0),
      error: () => this.runningFloridaCount.set(0),
    });
  }

  /** Lazy-load the row list on first expand; cached for the session. No fetch when the count is 0. */
  onFloridaAccordion(value: unknown): void {
    const opened = value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0);
    if (!opened || this.floridaLoaded() || !(this.runningFloridaCount() && this.runningFloridaCount()! > 0)) {
      return;
    }
    const filter = this.runningFloridaFilter;
    if (!filter) return;

    // QCT entity needs cycles expanded; the lab DTO returns them inline (and disallows $expand).
    let params = new HttpParams().set('$filter', filter).set('$orderby', 'certificationYear desc');
    if (this.isQct()) {
      params = params.set('$expand', 'cycles');
    }
    this.http.get<{ value: RunningFloridaRow[] }>(`odata/${this.floridaEntitySet}`, { params }).subscribe({
      next: (res) => {
        this.runningFloridaReports.set(res.value ?? []);
        this.floridaLoaded.set(true);
      },
      error: () => this.floridaLoaded.set(true),
    });
  }

  floridaCycleLabel(report: RunningFloridaRow): string {
    if (report.state === 'PendingInitial') return 'Initial';
    const open = (report.cycles ?? []).find((c) => c.state === 'PendingFinal');
    return open ? `${open.cycleMonths} mo` : '—';
  }

  viewFloridaReport(id: string): void {
    this.router.navigate(['/outdoor-exposure/florida', id]);
  }

  private loadDocuments(): void {
    const sys = this.system();
    if (!sys) return;

    const documentsService = this.odataFactory.entitySet<WorkflowDocument>('WorkflowDocuments');
    documentsService
      .entities()
      .query((q) => {
        // Fetch all documents except certificates
        q.filter(
          `EntityType eq '${EntityTypes.CertifiableSystem}' and EntityId eq ${sys.id} and DocumentType ne '${DocumentTypes.Certificate}'`
        );
        q.orderBy('CreatedAt desc');
      })
      .fetch()
      .subscribe({
        next: (response) => {
          this.documents.set(response.entities || []);
        },
        error: (error) => {
          console.error('Failed to load documents:', error);
        },
      });
  }

  private buildActionMenuItems(): ActionMenuItem[] {
    const sys = this.system();
    if (!sys) {
      return [];
    }

    const items: ActionMenuItem[] = [];
    const state = sys.approvalState || 'Granting';
    const canManage = this.canManageSystem();

    // State transitions based on current state
    switch (state) {
      case 'Granting':
        // Class-3 (10-year Florida) systems also use GrantProvisional; their P-number is
        // assigned at this transition (Number is null while in Granting). They then follow
        // the standard Provisional -> Interim -> Final path like every other class.
        items.push({
          label: 'Grant Provisional Approval',
          icon: 'pi pi-check',
          command: () => this.onTransition('GrantProvisional'),
          visible: canManage,
        });
        break;

      case 'Provisional':
        items.push({
          label: 'Promote to Interim',
          icon: 'pi pi-arrow-up',
          command: () => this.onTransition('PromoteToInterim'),
          visible: canManage,
        });
        break;

      case 'Interim':
        items.push({
          label: 'Promote to Final',
          icon: 'pi pi-check-circle',
          command: () => this.onTransition('PromoteToFinal'),
          visible: canManage,
        });
        break;

      case 'Final':
        // Renew is shown as a separate button in the header, not in the menu
        break;
    }

    // Certificate actions for approved states
    if (['Provisional', 'Interim', 'Final'].includes(state)) {
      const hasCertificates = this.certificates().length > 0;
      items.push({
        label: hasCertificates ? 'Re-generate Certificate' : 'Generate Certificate',
        icon: 'pi pi-file-pdf',
        command: () => this.onGenerateCertificate(),
        visible: canManage,
      });
    }

    // Cancel action for non-cancelled states
    if (state !== 'Cancelled') {
      items.push({
        label: 'Cancel Approval',
        icon: 'pi pi-times',
        danger: true,
        command: () => this.onCancel(),
        visible: canManage,
      });
    }

    return items;
  }

  // Permission checks
  canManageSystem(): boolean {
    return this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin');
  }

  canRenew(): boolean {
    const sys = this.system();
    const validStates = ['Provisional', 'Interim', 'Final'];
    return this.canManageSystem() && !!sys?.approvalState && validStates.includes(sys.approvalState);
  }

  hasVisibleActions(): boolean {
    return this.actionMenuItems().some((item) => item.visible);
  }

  // Action handlers
  onTransition(trigger: string): void {
    const sys = this.system();
    if (!sys) return;

    const targetState = this.getTargetState(sys.approvalState || 'Granting', trigger);

    const isPromotion = trigger === 'PromoteToInterim' || trigger === 'PromoteToFinal';
    const requiresDate = trigger === 'GrantProvisional';
    const approvalNumber =
      sys.number != null ? sys.completeApprovalNumber || `${sys.prefix}-${sys.number}` : `${sys.prefix} (pending)`;
    const dialogData: TransitionDialogData = {
      systemName: sys.system || 'Unknown System',
      approvalNumber,
      currentState: sys.approvalState || 'Granting',
      trigger,
      targetState,
      requiresDate,
      dateLabel: 'Date of Granting',
      showOptionalFields: isPromotion, // Show comment field for promotions
    };

    const ref = this.dialogService.open(TransitionDialogComponent, {
      header: `Transition to ${targetState}`,
      data: dialogData,
      position: 'right',
      height: '100%',
      width: '400px',
      modal: true,
      draggable: false,
      closable: true,
      dismissableMask: true,
    });

    ref?.onClose.subscribe((result: TransitionDialogResult) => {
      if (result?.confirmed) {
        this.executeTransition(trigger, result);
      }
    });
  }

  private executeTransition(trigger: string, dialogResult: TransitionDialogResult): void {
    const sys = this.system();
    if (!sys) return;

    const isPromotion = trigger === 'PromoteToInterim' || trigger === 'PromoteToFinal';

    // Upload files first if any, then execute the transition
    const files = dialogResult.files || [];
    const uploadObservables = files.map((file) =>
      this.documentService.uploadDocumentFile(
        EntityTypes.CertifiableSystem,
        sys.id!,
        file,
        DocumentTypes.TransitionDocument
      )
    );

    // Wait for all file uploads, then execute transition
    const uploads$ = files.length > 0 ? forkJoin(uploadObservables) : of([]);

    uploads$.subscribe({
      next: (uploadResults) => {
        // Extract document IDs from upload results
        const documentIds = uploadResults.map((result) => result.id);

        const request = {
          trigger,
          approvalDate:
            trigger === 'GrantProvisional' && dialogResult.date ? dialogResult.date.toISOString() : undefined,
          outdoorExposureTestStartDate: undefined,
          comment: isPromotion ? dialogResult.comment : undefined,
          documentIds: documentIds.length > 0 ? documentIds : undefined,
        };

        this.approvedSystemsService.transitionState(sys.id!, request).subscribe({
          next: (result) => {
            const fileMsg = files.length > 0 ? ` (${files.length} document(s) attached)` : '';
            this.toastService.displaySuccessMessage(
              `Successfully transitioned ${result.completeApprovalNumber} from ${result.previousState} to ${result.newState}${fileMsg}`
            );
            this.loadSystem(sys.id!, true);
          },
          error: (error) => {
            this.toastService.displayErrorMessage(error.error?.message || 'Failed to transition the system state');
          },
        });
      },
      error: (error) => {
        this.toastService.displayErrorMessage(error.error?.message || 'Failed to upload documents');
      },
    });
  }

  onGenerateCertificate(): void {
    const sys = this.system();
    if (!sys) return;

    this.generatingCertificate.set(true);
    this.approvedSystemsService.generateCertificate(sys.id!).subscribe({
      next: (result) => {
        this.generatingCertificate.set(false);
        this.toastService.displaySuccessMessage(
          `Certificate ${result.fileName} has been generated for ${result.approvalNumber}`
        );
        this.loadCertificates();
      },
      error: (error) => {
        this.generatingCertificate.set(false);
        this.toastService.displayErrorMessage(error.error?.message || 'Failed to generate certificate');
      },
    });
  }

  async onRenewCertificate(): Promise<void> {
    const sys = this.system();
    if (!sys) return;

    const { CertificateRenewalDialogComponent } =
      await import('../../components/certificate-renewal-dialog/certificate-renewal-dialog.component');

    const dialogRef = this.dialogService.open(CertificateRenewalDialogComponent, {
      header: 'Renew Certificate',
      position: 'right',
      height: '100%',
      width: '450px',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: { system: sys },
    });

    dialogRef?.onClose.subscribe((result) => {
      if (result?.renewed) {
        this.loadSystem(sys.id!, true);
      }
    });
  }

  onCancel(): void {
    const sys = this.system();
    if (!sys) return;

    const dialogData: TransitionDialogData = {
      systemName: sys.system || 'Unknown System',
      approvalNumber: sys.completeApprovalNumber || `${sys.prefix}-${sys.number}`,
      currentState: sys.approvalState || 'Granting',
      trigger: 'Cancel',
      targetState: 'Cancelled',
      requiresReason: true,
      requiresCancellationDate: true,
    };

    const ref = this.dialogService.open(TransitionDialogComponent, {
      header: 'Cancel Approval',
      data: dialogData,
      position: 'right',
      height: '100%',
      width: '400px',
      modal: true,
      draggable: false,
      closable: true,
      dismissableMask: true,
    });

    ref?.onClose.subscribe((result: TransitionDialogResult) => {
      if (result?.confirmed && result.reason) {
        // Upload files first if any, then cancel
        const files = result.files || [];
        const uploadObservables = files.map((file) =>
          this.documentService.uploadDocumentFile(
            EntityTypes.CertifiableSystem,
            sys.id!,
            file,
            DocumentTypes.TransitionDocument
          )
        );

        const uploads$ = files.length > 0 ? forkJoin(uploadObservables) : of([]);

        uploads$.subscribe({
          next: (uploadResults) => {
            const documentIds = uploadResults.map((r) => r.id);

            this.approvedSystemsService
              .cancelSystem(sys.id!, result.reason!, result.cancellationDate, documentIds)
              .subscribe({
                next: (response) => {
                  const fileMsg = files.length > 0 ? ` (${files.length} document(s) attached)` : '';
                  this.toastService.displaySuccessMessage(
                    `${response.completeApprovalNumber} has been cancelled${fileMsg}`
                  );
                  this.loadSystem(sys.id!, true);
                },
                error: (error) => {
                  this.toastService.displayErrorMessage(error.error?.message || 'Failed to cancel the approval');
                },
              });
          },
          error: (error) => {
            this.toastService.displayErrorMessage(error.error?.message || 'Failed to upload documents');
          },
        });
      }
    });
  }

  private getTargetState(currentState: string, trigger: string): string {
    const transitions: Record<string, Record<string, string>> = {
      Granting: { GrantProvisional: 'Provisional' },
      Provisional: { PromoteToInterim: 'Interim' },
      Interim: { PromoteToFinal: 'Final' },
    };
    return transitions[currentState]?.[trigger] || 'Unknown';
  }

  // Status helpers
  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const state = this.system()?.approvalState || 'Granting';
    switch (state) {
      case 'Final':
        return 'success';
      case 'Interim':
        return 'info';
      case 'Provisional':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      case 'Granting':
      default:
        return 'secondary';
    }
  }

  // Date formatting
  formatDate(date: string | null | undefined): string {
    if (!date) return '-';
    return this.datePipe.transform(date, 'dd.MM.yyyy', 'UTC') || '-';
  }

  formatFileSize(bytes: number): string {
    return this.documentService.formatFileSize(bytes);
  }

  // Calculate years completed for outdoor exposure test
  calculateOutdoorExposureYears(startDate: string | null | undefined): number {
    if (!startDate) return 0;
    const diffMs = Date.now() - new Date(startDate).getTime();
    return Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000));
  }

  // Check if the current system is a CoatingSystem (powder coating)
  isCoatingSystem(): boolean {
    const sys = this.system();
    if (!sys) return false;
    // Check for CoatingSystem derived type via OData type annotation or prefix
    const odataType = (sys as unknown as Record<string, unknown>)['@odata.type'] as string | undefined;
    return odataType?.includes('CoatingSystem') || sys.prefix === 'P' || sys.prefix === 'DP';
  }

  // Get the system as CoatingSystemData for the powder attributes component
  getCoatingSystemData(): CoatingSystemData | null {
    if (!this.isCoatingSystem()) return null;
    return this.system() as unknown as CoatingSystemData;
  }

  // Get the system as PretreatmentSystem for pre-treatment attributes
  getPretreatmentSystem(): PretreatmentSystem | null {
    if (this.isCoatingSystem()) return null;
    return this.system() as unknown as PretreatmentSystem;
  }

  // Certificate actions
  viewCertificate(certificate: WorkflowDocument): void {
    if (certificate.objectStoreRef) {
      this.documentService.viewDocument(
        certificate.objectStoreRef,
        certificate.fileName,
        certificate.fileName,
        certificate.mimeType
      );
    }
  }

  downloadCertificate(certificate: WorkflowDocument): void {
    if (certificate.objectStoreRef) {
      this.documentService.downloadDocument(certificate.objectStoreRef, certificate.fileName);
    }
  }

  downloadCertificateInLanguage(language: string): void {
    const sys = this.system();
    if (!sys) return;

    const downloadUrl = `/api/v1/certifiable-systems/${sys.id}/certificate?language=${language}`;
    const fileName = `Certificate_${sys.completeApprovalNumber}_${language.toUpperCase()}.pdf`;
    this.documentService.downloadFromUrl(downloadUrl, fileName);
  }

  // Document actions
  viewDoc(document: WorkflowDocument): void {
    if (document.objectStoreRef) {
      this.documentService.viewDocument(
        document.objectStoreRef,
        document.fileName,
        document.fileName,
        document.mimeType
      );
    }
  }

  downloadDoc(document: WorkflowDocument): void {
    if (document.objectStoreRef) {
      this.documentService.downloadDocument(document.objectStoreRef, document.fileName);
    }
  }

  onUploadDocuments(): void {
    const sys = this.system();
    if (!sys || this.selectedFiles.length === 0) return;

    this.uploadingDocuments.set(true);

    const uploadObservables = this.selectedFiles.map((file) =>
      this.documentService.uploadDocumentFile(EntityTypes.CertifiableSystem, sys.id!, file, DocumentTypes.Other)
    );

    forkJoin(uploadObservables).subscribe({
      next: (results) => {
        this.uploadingDocuments.set(false);
        this.toastService.displaySuccessMessage(`Successfully uploaded ${results.length} document(s)`);
        this.selectedFiles = [];
        this.loadDocuments();
      },
      error: (error) => {
        this.uploadingDocuments.set(false);
        this.toastService.displayErrorMessage(error.error?.message || 'Failed to upload documents');
      },
    });
  }

  // Document type helpers (delegated to DocumentService)
  getDocumentTypeLabel(type: string): string {
    return this.documentService.getDocumentTypeLabel(type);
  }

  getDocumentTypeIcon(type: string): [string, string] {
    return this.documentService.getDocumentTypeIcon(type);
  }

  getDocumentTypeColor(type: string): string {
    return this.documentService.getDocumentTypeColor(type);
  }

  isViewableDocument(document: WorkflowDocument): boolean {
    const mimeType = document.mimeType?.toLowerCase();
    const fileName = document.fileName?.toLowerCase();

    // PDF
    if (mimeType === 'application/pdf' || fileName?.endsWith('.pdf')) {
      return true;
    }

    // Word documents
    const docxMimeTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (mimeType && docxMimeTypes.includes(mimeType)) {
      return true;
    }
    if (fileName?.endsWith('.docx') || fileName?.endsWith('.doc')) {
      return true;
    }

    return false;
  }

  // Get file type icon based on mime type or file extension
  getFileTypeIcon(document: WorkflowDocument): [string, string] {
    const mimeType = document.mimeType?.toLowerCase() || '';
    const fileName = document.fileName?.toLowerCase() || '';

    // PDF
    if (mimeType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return ['fas', 'file-pdf'];
    }

    // Word documents
    if (
      mimeType.includes('wordprocessingml') ||
      mimeType === 'application/msword' ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.doc')
    ) {
      return ['fas', 'file-word'];
    }

    // Excel documents
    if (
      mimeType.includes('spreadsheetml') ||
      mimeType === 'application/vnd.ms-excel' ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls')
    ) {
      return ['fas', 'file-excel'];
    }

    // PowerPoint documents
    if (
      mimeType.includes('presentationml') ||
      mimeType === 'application/vnd.ms-powerpoint' ||
      fileName.endsWith('.pptx') ||
      fileName.endsWith('.ppt')
    ) {
      return ['fas', 'file-powerpoint'];
    }

    // Images
    if (
      mimeType.startsWith('image/') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp')
    ) {
      return ['fas', 'file-image'];
    }

    // Default
    return ['fas', 'file'];
  }

  // Get file type color based on mime type or file extension
  getFileTypeColor(document: WorkflowDocument): string {
    const mimeType = document.mimeType?.toLowerCase() || '';
    const fileName = document.fileName?.toLowerCase() || '';

    // PDF - red
    if (mimeType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return 'red';
    }

    // Word - blue
    if (
      mimeType.includes('wordprocessingml') ||
      mimeType === 'application/msword' ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.doc')
    ) {
      return 'blue';
    }

    // Excel - green
    if (
      mimeType.includes('spreadsheetml') ||
      mimeType === 'application/vnd.ms-excel' ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls')
    ) {
      return 'green';
    }

    // PowerPoint - orange
    if (
      mimeType.includes('presentationml') ||
      mimeType === 'application/vnd.ms-powerpoint' ||
      fileName.endsWith('.pptx') ||
      fileName.endsWith('.ppt')
    ) {
      return 'orange';
    }

    // Images - purple
    if (
      mimeType.startsWith('image/') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp')
    ) {
      return 'purple';
    }

    // Default - gray
    return 'gray';
  }

  // Production Site actions
  onEditProductionSite(): void {
    const sys = this.system();
    if (!sys?.productionSite) {
      console.error('No production site available to edit');
      return;
    }

    // Import the production site form component dynamically
    import('../../../../crm/pages/organisation/production-sites/production-site-form/production-site-form.component').then(
      ({ ProductionSiteFormComponent }) => {
        // Fetch the full production site WITH coating lines
        const productionSitesService = this.odataFactory.entitySet<ProductionSite>('ProductionSites');
        productionSitesService
          .entity(sys.productionSite!.id)
          .query((q) => {
            q.expand('CoatingLines');
          })
          .fetch()
          .subscribe({
            next: (productionSiteResponse) => {
              const fullProductionSite = productionSiteResponse.entity;

              if (!fullProductionSite) {
                console.error('Production site not found');
                this.toastService.displayErrorMessage('Production site not found.');
                return;
              }

              // Fetch contacts for the customer organization
              const contactsService = this.odataFactory.entitySet<Contact>('Contacts');
              contactsService
                .entities()
                .query((q) => {
                  q.filter(`OrganisationId eq ${sys.productionSite?.customerId}`);
                  q.select('Id,FirstName,LastName,Email');
                  q.orderBy('LastName,FirstName');
                })
                .fetch()
                .subscribe({
                  next: (contactsResponse) => {
                    const contactsForSelect = [
                      { label: 'None', value: null },
                      ...(contactsResponse.entities || []).map((contact: Contact) => ({
                        label: `${contact.firstName} ${contact.lastName}`,
                        value: contact.id,
                      })),
                    ];

                    // Open the production site form dialog with full data
                    const dialogRef = this.dialogService.open(ProductionSiteFormComponent, {
                      header: `Edit Production Site: ${fullProductionSite.name}`,
                      position: 'right',
                      height: '100%',
                      width: '700px',
                      modal: true,
                      draggable: false,
                      closable: true,
                      maskStyleClass: 'mask',
                      contentStyle: { overflow: 'auto' },
                      data: {
                        productionSite: fullProductionSite,
                        contactsForSelect: contactsForSelect,
                      },
                    });

                    dialogRef?.onClose.subscribe((result) => {
                      if (result && sys.productionSite) {
                        // Save the updated production site to the backend
                        this.productionSiteDataService
                          .update(sys.productionSite.id, result)
                          .pipe(take(1))
                          .subscribe({
                            next: () => {
                              this.toastService.displaySuccessMessage('Production site updated successfully.');
                              // Reload the system to get updated production site data
                              this.loadSystem(sys.id!, true);
                            },
                            error: (error) => {
                              console.error('Failed to update production site:', error);
                              this.toastService.displayErrorMessage('Failed to update production site.');
                            },
                          });
                      }
                    });
                  },
                  error: (error) => {
                    console.error('Failed to fetch contacts:', error);
                    this.toastService.displayErrorMessage('Failed to load contacts.');
                  },
                });
            },
            error: (error) => {
              console.error('Failed to fetch production site:', error);
              this.toastService.displayErrorMessage('Failed to load production site details.');
            },
          });
      }
    );
  }

  openMap(): void {
    const sys = this.system();
    if (!sys?.productionSite) return;

    // Build the address string for Google Maps
    const addressParts = [];
    const site = sys.productionSite;

    if (site.addressLine1) addressParts.push(site.addressLine1);
    if (site.addressLine2) addressParts.push(site.addressLine2);
    if (site.postalCode) addressParts.push(site.postalCode);
    if (site.city) addressParts.push(site.city);
    if (site.countryName) addressParts.push(site.countryName);

    const fullAddress = addressParts.join(', ');

    if (fullAddress) {
      // Open Google Maps in a new tab with the address
      const encodedAddress = encodeURIComponent(fullAddress);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  }

  /**
   * Handle system name change from inline edit
   */
  onSystemNameChanged(newName: string): void {
    const sys = this.system();
    if (!sys || !newName.trim()) return;

    const trimmedName = newName.trim();

    // Skip if no actual change
    if (trimmedName === sys.system) return;

    this.approvedSystemsService.updateName(sys.id!, trimmedName).subscribe({
      next: (result) => {
        this.toastService.displaySuccessMessage(
          `System name updated from "${result.previousName || '-'}" to "${result.newName}"`
        );
        this.loadSystem(sys.id!, true);
      },
      error: (error) => {
        this.toastService.displayErrorMessage(error.error?.message || 'Failed to update system name');
      },
    });
  }

  // System Attributes actions
  async onEditAttributes(): Promise<void> {
    const sys = this.system();
    if (!sys) return;

    const { SystemAttributesEditorComponent } =
      await import('../../components/system-attributes-editor/system-attributes-editor.component');

    const dialogRef = this.dialogService.open(SystemAttributesEditorComponent, {
      header: 'Edit System Attributes',
      position: 'right',
      height: '100%',
      width: '400px',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: { system: sys },
    });

    dialogRef?.onClose.subscribe((result) => {
      if (result) {
        this.toastService.displaySuccessMessage('System attributes updated successfully.');
        // Reload system to get updated data
        this.loadSystem(sys.id!, true);
      }
    });
  }

  // Powder Coating Attributes actions
  async onEditPowderAttributes(): Promise<void> {
    const sys = this.system();
    if (!sys || !this.isCoatingSystem()) return;

    const { PowderAttributesEditorComponent } =
      await import('../../components/powder-attributes-editor/powder-attributes-editor.component');

    const coatingData = this.getCoatingSystemData();
    const dialogRef = this.dialogService.open(PowderAttributesEditorComponent, {
      header: 'Edit Powder Coating Attributes',
      position: 'right',
      height: '100%',
      width: '450px',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'visible' },
      data: {
        system: {
          ...coatingData,
          id: sys.id,
          completeApprovalNumber: sys.completeApprovalNumber,
          supplierOrManufacturerOrganisationId: sys.supplierOrManufacturerOrganisationId,
        },
      },
    });

    dialogRef?.onClose.subscribe((result) => {
      if (result) {
        this.toastService.displaySuccessMessage('Powder coating attributes updated successfully.');
        // Reload system to get updated data
        this.loadSystem(sys.id!, true);
      }
    });
  }

  // Add Color Restriction
  async onAddColor(): Promise<void> {
    const sys = this.system();
    if (!sys || !this.isCoatingSystem()) return;

    const { CoatingSystemColorEditorComponent } =
      await import('../../components/coating-system-color-editor/coating-system-color-editor.component');

    const coatingData = this.getCoatingSystemData();
    const existingColorNumbers = coatingData?.coatingSystemColors?.map((c) => c.colorNumber) || [];

    const dialogData: ColorEditorData = {
      mode: 'add',
      systemId: sys.id!,
      approvalNumber: sys.completeApprovalNumber || `${sys.prefix}-${sys.number}`,
      existingColorNumbers,
    };

    const dialogRef = this.dialogService.open(CoatingSystemColorEditorComponent, {
      header: 'Add Colour Restriction',
      position: 'right',
      height: '100%',
      width: '500px',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: dialogData,
    });

    dialogRef?.onClose.subscribe((result) => {
      if (result) {
        this.toastService.displaySuccessMessage(`Color ${result.colorNumber} added successfully.`);
        this.loadSystem(sys.id!, true);
      }
    });
  }

  // Edit Color Restriction
  async onEditColorRestriction(color: CoatingSystemColor): Promise<void> {
    const sys = this.system();
    if (!sys || !this.isCoatingSystem()) return;

    const { CoatingSystemColorEditorComponent } =
      await import('../../components/coating-system-color-editor/coating-system-color-editor.component');

    const dialogData: ColorEditorData = {
      mode: 'edit',
      systemId: sys.id!,
      approvalNumber: sys.completeApprovalNumber || `${sys.prefix}-${sys.number}`,
      existingColor: color,
    };

    const dialogRef = this.dialogService.open(CoatingSystemColorEditorComponent, {
      header: `Edit Color: ${color.colorNumber}`,
      position: 'right',
      height: '100%',
      width: '500px',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: dialogData,
    });

    dialogRef?.onClose.subscribe((result) => {
      if (result) {
        this.toastService.displaySuccessMessage(`Color ${result.colorNumber} updated successfully.`);
        this.loadSystem(sys.id!, true);
      }
    });
  }
}

/** Minimal shape of a running Florida Report row (local to avoid a cross-domain model import). */
interface RunningFloridaRow {
  id: string;
  certificationYear: number;
  class: number;
  state: string;
  cycles?: { cycleMonths: number; state: string }[];
}
